<template>
  <div class="content" v-loading="loading">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-7 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t('member_admin.title') }}
            <span v-if="profile.id" class="text-white-50 ml-2">
              — {{ profile.fname }} {{ profile.lname }} <small>#{{ profile.id }}</small>
            </span>
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-5 col-5 text-right">
          <base-button size="sm" type="neutral" @click="$router.back()">
            <i class="fa fa-arrow-left"></i> {{ $t('back') || 'Back' }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- Summary card -->
      <div class="row" v-if="profile.id">
        <div class="col-md-3">
          <stats-card
            :sub-title="profile.phone || '-'"
            title="Phone"
            type="gradient-primary"
            icon="ni ni-mobile-button"
          ></stats-card>
        </div>
        <div class="col-md-3">
          <stats-card
            :sub-title="profile.email || '-'"
            title="Email"
            type="gradient-info"
            icon="ni ni-email-83"
          ></stats-card>
        </div>
        <div class="col-md-3">
          <stats-card
            :sub-title="fmtNum(profile.total_spending)"
            title="Total spending"
            type="gradient-success"
            icon="ni ni-money-coins"
          ></stats-card>
        </div>
        <div class="col-md-3">
          <stats-card
            :sub-title="pointsBalance !== null ? fmtNum(pointsBalance) : '-'"
            title="Points balance"
            type="gradient-warning"
            icon="ni ni-trophy"
          ></stats-card>
        </div>
      </div>

      <card class="no-border-card member-detail-card" body-classes="px-lg-4 py-lg-4">
        <el-tabs v-model="activeTab" @tab-click="onTabChange">
        <el-tab-pane :label="$t('member_admin.tabs.profile')" name="profile">
          <div class="row mt-3" v-if="profile.id">
            <div class="col-md-6">
              <h6 class="heading-small text-muted mb-2">{{ $t('member_admin.personal_info') || 'Personal info' }}</h6>
              <hr class="mt-0 mb-3" />
              <div class="row">
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.first_name') || 'First name'" v-model="edit.fname" :readonly="!canEdit"></base-input>
                </div>
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.last_name') || 'Last name'" v-model="edit.lname" :readonly="!canEdit"></base-input>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.phone') || 'Phone'" v-model="edit.phone" :readonly="!canEdit"></base-input>
                </div>
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.email') || 'Email'" v-model="edit.email" :readonly="!canEdit"></base-input>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.line_user_id') || 'LINE user ID'" :value="profile.line_user_id || '-'" readonly></base-input>
                </div>
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.member_type') || 'Member type'">
                    <el-select v-model="edit.member_type" :disabled="!canEdit" style="width: 100%">
                      <el-option :label="$t('member_admin.member_type_company') || 'Company'" :value="1"></el-option>
                      <el-option :label="$t('member_admin.member_type_agent') || 'Agent'" :value="2"></el-option>
                      <el-option :label="$t('member_admin.member_type_platform') || 'Platform'" :value="3"></el-option>
                    </el-select>
                  </base-input>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6 offset-md-6">
                  <div class="form-group">
                    <label class="form-control-label">{{ $t('birthday') || 'Birthday' }}</label>
                    <div>
                      <base-button v-if="canEdit" type="primary" size="sm" outline @click="openBirthdayModal()">
                        <i class="ni ni-calendar-grid-58 mr-1"></i>
                        {{ edit.birthday ? ($t('edit') + ' ' + $t('birthday')) : ($t('birthday_set') || 'Set birthday') }}
                      </base-button>
                      <base-button v-if="canEdit && edit.birthday" type="danger" size="sm" outline class="ml-2" @click="clearBirthday()">
                        <i class="fa fa-times mr-1"></i>
                        {{ $t('birthday_clear') || 'Clear' }}
                      </base-button>
                      <span v-if="edit.birthday" class="ml-2 text-default">
                        {{ formatBirthdayBE(edit.birthday) }}
                      </span>
                      <span v-else-if="!canEdit" class="text-muted">-</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <h6 class="heading-small text-muted mb-2">{{ $t('member_admin.address') || 'Address' }}</h6>
              <hr class="mt-0 mb-3" />
              <div class="row">
                <div class="col-md-12">
                  <base-input :label="$t('member_admin.address') || 'Address'"
                              v-model="edit.address"
                              :readonly="!canEdit"
                              maxlength="500"></base-input>
                </div>
              </div>
              <div class="row" v-if="canEdit">
                <div class="col-md-12">
                  <div class="form-group">
                    <label class="form-control-label">{{ $t('member_admin.address_search') || 'Address search (district / zipcode)' }}</label>
                    <base-input
                      v-model="profileAddrQuery"
                      :placeholder="$t('member_admin.address_search') || 'Type district or zipcode'"
                      @input="profileAddrOpen = true"
                    ></base-input>
                    <ul v-show="profileAddrOpen && profileFilteredAddress.length" class="autocomplete-results">
                      <li
                        v-for="(result, i) in profileFilteredAddress"
                        :key="i"
                        @click="setProfileAddrResult(result)"
                        class="autocomplete-result"
                      >
                        <template v-if="result.ProvinceID == 10">
                          แขวง{{ result.District }} เขต{{ result.City }} จังหวัด{{ result.Province }} {{ result.Zipcode }}
                        </template>
                        <template v-else>
                          ต.{{ result.District }} อ.{{ result.City }} จ.{{ result.Province }} {{ result.Zipcode }}
                        </template>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.subdistrict') || 'Subdistrict'" v-model="edit.subdistrict" :readonly="!canEdit"></base-input>
                </div>
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.district') || 'District'" v-model="edit.district" :readonly="!canEdit"></base-input>
                </div>
              </div>
              <div class="row">
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.province') || 'Province'" v-model="edit.province" :readonly="!canEdit"></base-input>
                </div>
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.postcode') || 'Postcode'" v-model="edit.postcode" :readonly="!canEdit"></base-input>
                </div>
              </div>
              <div class="row" v-if="level">
                <div class="col-md-6">
                  <base-input :label="$t('member_admin.liff_level') || 'LIFF level'" :value="(level && (level.name || level.code)) || '-'" readonly></base-input>
                </div>
                <div class="col-md-6" v-if="pointsBalance !== null">
                  <base-input :label="$t('member_admin.points_balance') || 'Points balance'" :value="fmtNum(pointsBalance)" readonly></base-input>
                </div>
              </div>
              <!-- Coin (D3-3). Read-only here on purpose: the only way to move a
                   coin balance from the BO is the audited "ปรับ coin" dialog on
                   the Coin tab. Hidden entirely when the API did not return a
                   coin_balance, so this page still renders against a build that
                   predates the coin ledger. -->
              <div class="row" v-if="coinBalance !== null">
                <div class="col-md-6">
                  <base-input :label="$t('member_coins.balance')" :value="fmtNum(coinBalance)" readonly></base-input>
                </div>
                <div class="col-md-6">
                  <base-input :label="$t('member_coins.lifetime_earned')" :value="fmtNum(coinLifetimeEarned)" readonly></base-input>
                </div>
              </div>
            </div>
          </div>

          <div class="text-right mt-3" v-if="canEdit">
            <base-button type="primary" size="sm" @click="save">
              <i class="fa fa-save"></i> {{ $t('save') || 'Save' }}
            </base-button>
          </div>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.addresses')" name="addresses" lazy v-if="false">
          <div class="d-flex justify-content-end mb-2" v-if="canEdit">
            <base-button type="primary" size="sm" @click="openAddressCreate">
              <i class="fa fa-plus"></i> {{ $t('member_admin.address_add') }}
            </base-button>
          </div>
          <el-table :data="addresses" v-loading="loadingAddresses" stripe>
            <el-table-column label="#" prop="id" width="80"></el-table-column>
            <el-table-column :label="$t('member_admin.fname') + ' / ' + $t('member_admin.last_name')" min-width="180">
              <template slot-scope="{row}">{{ (row.fname || '') + ' ' + (row.lname || '') }}</template>
            </el-table-column>
            <el-table-column :label="$t('member_admin.phone')" prop="phone" min-width="130"></el-table-column>
            <el-table-column :label="$t('member_admin.address')" prop="address" min-width="240"></el-table-column>
            <el-table-column :label="$t('member_admin.subdistrict')" prop="subdistrict" width="120"></el-table-column>
            <el-table-column :label="$t('member_admin.district')" prop="district" width="120"></el-table-column>
            <el-table-column :label="$t('member_admin.province')" prop="province" width="120"></el-table-column>
            <el-table-column :label="$t('member_admin.postcode')" prop="postcode" width="90"></el-table-column>
            <el-table-column :label="$t('member_admin.default')" width="90" align="center">
              <template slot-scope="{row}">
                <i v-if="row.is_default == 1" class="fa fa-check text-success"></i>
              </template>
            </el-table-column>
            <el-table-column label="" width="120" align="center" v-if="canEdit">
              <template slot-scope="{row}">
                <base-button type="info" size="sm" icon @click.native="openAddressEdit(row)" :title="$t('edit')">
                  <i class="fa fa-edit text-white"></i>
                </base-button>
                <base-button type="danger" size="sm" icon @click.native="deleteAddress(row)" :title="$t('delete')">
                  <i class="fa fa-trash text-white"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.points')" name="points" lazy v-if="false">
          <div class="mb-2"><b>Balance:</b> {{ fmtNum(pointsBalance) }}</div>
          <el-table :data="points" v-loading="loadingPoints" stripe>
            <el-table-column label="#" prop="id" width="60"></el-table-column>
            <el-table-column label="Delta" align="right" width="100">
              <template slot-scope="{row}">
                <span :class="row.delta >= 0 ? 'text-success' : 'text-danger'">{{ fmtNum(row.delta) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="Reason" prop="reason" min-width="160"></el-table-column>
            <el-table-column label="Reference" prop="reference" min-width="180"></el-table-column>
            <el-table-column label="Expires at" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.expires_at) }}</template>
            </el-table-column>
            <el-table-column label="Created at" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- Coin ledger (D3-3). Visible tab, unlike the legacy points/coupons
             panes above: this is the screen support uses to explain a balance
             and the only place a coin adjustment can be made. -->
        <el-tab-pane :label="$t('member_coins.tab')" name="coins" lazy>
          <div class="row align-items-center mb-3">
            <div class="col-md-8">
              <span class="text-muted text-uppercase small mr-2">{{ $t('member_coins.balance') }}</span>
              <span class="h2 font-weight-bold mb-0 mr-4">{{ fmtNum(coinBalance) }}</span>
              <span class="text-muted text-uppercase small mr-2">{{ $t('member_coins.lifetime_earned') }}</span>
              <span class="h4 font-weight-bold mb-0">{{ fmtNum(coinLifetimeEarned) }}</span>
            </div>
            <div class="col-md-4 text-right">
              <base-button v-if="canEdit" type="primary" size="sm" @click="openCoinAdjust">
                <i class="ni ni-money-coins mr-1"></i> {{ $t('member_coins.adjust_btn') }}
              </base-button>
            </div>
          </div>

          <el-table :data="coinRows" v-loading="loadingCoins" stripe
                    :empty-text="$t('member_coins.no_rows')">
            <el-table-column label="#" prop="id" width="80"></el-table-column>
            <el-table-column :label="$t('member_coins.col_type')" width="130">
              <template slot-scope="{row}">
                <span class="badge" :class="coinTypeBadgeClass(row.type)">
                  {{ $t(coinMeta(row).typeLabelKey) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('member_coins.col_coins')" align="right" width="120">
              <template slot-scope="{row}">
                <span class="font-weight-bold" :class="coinMeta(row).colorClass">
                  {{ coinMeta(row).sign }}{{ fmtNum(Math.abs(Number(row.coins) || 0)) }}
                </span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('member_coins.col_balance_after')" align="right" width="130">
              <template slot-scope="{row}">{{ fmtNum(row.balance_after) }}</template>
            </el-table-column>
            <el-table-column :label="$t('member_coins.col_ref')" min-width="150">
              <template slot-scope="{row}">{{ coinRefText(row) }}</template>
            </el-table-column>
            <el-table-column :label="$t('member_coins.col_note')" prop="note" min-width="220"></el-table-column>
            <el-table-column :label="$t('member_coins.col_expires_at')" width="130">
              <template slot-scope="{row}">{{ coinMeta(row).expiresText || '-' }}</template>
            </el-table-column>
            <el-table-column :label="$t('member_coins.col_created_at')" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.coupons')" name="coupons" lazy v-if="false">
          <el-table :data="coupons" v-loading="loadingCoupons" stripe>
            <el-table-column label="#" prop="id" width="60"></el-table-column>
            <el-table-column label="Code" prop="code" min-width="160"></el-table-column>
            <el-table-column label="Template" prop="template_id" width="100"></el-table-column>
            <el-table-column label="Status" prop="status" width="120"></el-table-column>
            <el-table-column label="Issued at" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.issued_at || row.created_at) }}</template>
            </el-table-column>
            <el-table-column label="Expires at" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.expires_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.redemptions')" name="redemptions" lazy v-if="false">
          <el-table :data="redemptions" v-loading="loadingRedemptions" stripe>
            <el-table-column label="#" prop="id" width="60"></el-table-column>
            <el-table-column label="Coupon" prop="coupon_id" width="100"></el-table-column>
            <el-table-column label="Order ref" prop="order_ref" min-width="140"></el-table-column>
            <el-table-column label="Amount" align="right" width="120">
              <template slot-scope="{row}">{{ fmtNum(row.amount) }}</template>
            </el-table-column>
            <el-table-column label="Redeemed at" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.redeemed_at || row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <!-- Affiliate eligibility (owner 2026-10-16): only members granted here
             see the Affiliate menu on www / LINE. Same roles as the affiliate
             pages: marketing reads + writes, affiliate_finance reads. -->
        <el-tab-pane v-if="canSeeAffiliate" :label="$t('member_admin.tabs.affiliate')" name="affiliate" lazy>
          <affiliate-eligibility-panel v-if="profile.id" :member-id="profile.id"></affiliate-eligibility-panel>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.orders')" name="orders" lazy>
          <!-- Search row: orders are matched by member.phone ↔ bo_orders.recipient_phone
               within the given created_at window. Single daterange picker
               mirroring the KOL Brief search pattern. -->
          <div class="row align-items-end mb-2">
            <div class="col-md-7">
              <base-input :label="$t('member_admin.order_date_range')">
                <div class="d-flex align-items-center">
                  <el-date-picker
                    v-model="ordersFilter.range"
                    type="daterange"
                    range-separator="-"
                    :start-placeholder="$t('member_admin.order_date_from')"
                    :end-placeholder="$t('member_admin.order_date_to')"
                    format="yyyy-MM-dd"
                    value-format="yyyy-MM-dd"
                    @change="searchOrders"
                  ></el-date-picker>
                  <base-button
                    type="primary"
                    size="sm"
                    class="ml-2 mb-0"
                    :loading="loadingOrders"
                    :disabled="!(ordersFilter.range && ordersFilter.range.length === 2)"
                    @click="searchOrders"
                  >
                    <i class="fa fa-search"></i> {{ $t('search') }}
                  </base-button>
                </div>
              </base-input>
            </div>
          </div>

          <el-table :data="orders" v-loading="loadingOrders" stripe :row-class-name="orderRowClass"
                    empty-text=" "
                    v-if="orders.length || ordersSearched">
            <el-table-column label="#" prop="id" width="100">
              <template slot-scope="{row}">
                <a href="javascript:void(0)" class="font-weight-bold text-primary" @click="openOrderDetail(row.id)">
                  {{ row.id }}
                </a>
              </template>
            </el-table-column>
            <el-table-column :label="$t('status')" prop="order_status" min-width="160">
              <template slot-scope="{row}">{{ orderStatusText(row.order_status) }}</template>
            </el-table-column>
            <el-table-column :label="$t('member_admin.tracking') || 'Tracking'" prop="shipping_tracking_number" min-width="180"></el-table-column>
            <el-table-column :label="$t('member_admin.total') || 'Total'" align="right" width="120">
              <template slot-scope="{row}">{{ fmtNum(row.price_total) }}</template>
            </el-table-column>
            <el-table-column :label="$t('member_admin.platform') || 'Platform'" prop="platform" min-width="120">
              <template slot-scope="{row}">{{ platformText(row.platform) }}</template>
            </el-table-column>
            <el-table-column :label="$t('member_admin.created') || 'Created'" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>

          <div v-else class="text-muted small p-3">
            <i class="fa fa-info-circle mr-1"></i>
            {{ $t('member_admin.orders_hint') }}
          </div>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.notifications')" name="notifications" lazy v-if="false">
          <el-table :data="notifications" v-loading="loadingNotifications" stripe>
            <el-table-column label="#" prop="id" width="60"></el-table-column>
            <el-table-column label="Title" prop="title" min-width="200"></el-table-column>
            <el-table-column label="Body" prop="body" min-width="300"></el-table-column>
            <el-table-column label="Channel" prop="channel" width="100"></el-table-column>
            <el-table-column label="Read" prop="is_read" width="80" align="center"></el-table-column>
            <el-table-column label="Created" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.sessions')" name="sessions" lazy v-if="false">
          <el-table :data="sessions" v-loading="loadingSessions" stripe>
            <el-table-column label="#" prop="id" width="60"></el-table-column>
            <el-table-column label="Device" prop="device" min-width="180"></el-table-column>
            <el-table-column label="IP" prop="ip" width="140"></el-table-column>
            <el-table-column label="Last seen" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.last_seen_at || row.updated_at) }}</template>
            </el-table-column>
            <el-table-column label="Created" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.events')" name="events" lazy v-if="false">
          <el-table :data="events" v-loading="loadingEvents" stripe>
            <el-table-column label="#" prop="id" width="60"></el-table-column>
            <el-table-column label="Event" prop="event_type" width="160"></el-table-column>
            <el-table-column label="Detail" prop="detail" min-width="320"></el-table-column>
            <el-table-column label="Created" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.pdpa')" name="pdpa" lazy v-if="false">
          <el-table :data="pdpa" v-loading="loadingPDPA" stripe>
            <el-table-column label="#" prop="id" width="60"></el-table-column>
            <el-table-column label="Version" prop="version" width="100"></el-table-column>
            <el-table-column label="Consents" prop="consents" min-width="320"></el-table-column>
            <el-table-column label="IP" prop="ip" width="140"></el-table-column>
            <el-table-column label="Consented at" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.consented_at || row.created_at) }}</template>
            </el-table-column>
          </el-table>
        </el-tab-pane>

        <el-tab-pane :label="$t('member_admin.tabs.legacy')" name="legacy" lazy v-if="false">
          <el-table :data="legacy" v-loading="loadingLegacy" stripe>
            <el-table-column label="#" prop="id" width="60"></el-table-column>
            <el-table-column label="Legacy member" prop="legacy_member_id" width="160"></el-table-column>
            <el-table-column label="Linked at" min-width="160">
              <template slot-scope="{row}">{{ fmtDateTime(row.linked_at || row.created_at) }}</template>
            </el-table-column>
            <el-table-column label="Note" prop="note" min-width="240"></el-table-column>
          </el-table>
        </el-tab-pane>
      </el-tabs>
      </card>
    </div>

    <!-- Address create / edit modal -->
    <modal :show.sync="addrModal.show" body-classes="p-0" modal-classes="modal-lg">
      <card type="secondary" header-classes="bg-white pb-5" body-classes="px-lg-5 py-lg-5" class="border-0 mb-0">
        <h3>{{ addrForm.id ? $t('member_admin.address_edit_title') : $t('member_admin.address_create_title') }}</h3>

        <div class="row">
          <div class="col-md-6">
            <base-input :label="$t('member_admin.fname')" v-model="addrForm.fname"></base-input>
          </div>
          <div class="col-md-6">
            <base-input :label="$t('member_admin.last_name')" v-model="addrForm.lname"></base-input>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <base-input :label="$t('member_admin.phone')" v-model="addrForm.phone"></base-input>
          </div>
          <div class="col-md-6">
            <base-input :label="$t('member_admin.address')" v-model="addrForm.address"></base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <base-input
              :label="$t('member_admin.address_search')"
              v-model="addrQuery"
              :placeholder="$t('member_admin.address_search_hint')"
              @input="addrOpen = !!addrQuery"
            ></base-input>
            <ul v-show="addrOpen && filteredAddress.length" class="autocomplete-results">
              <li
                v-for="(r, i) in filteredAddress"
                :key="i"
                class="autocomplete-result"
                @click="setAddrResult(r)"
              >
                <template v-if="r.ProvinceID == 10">แขวง{{ r.District }} เขต{{ r.City }} จังหวัด{{ r.Province }} {{ r.Zipcode }}</template>
                <template v-else>ต.{{ r.District }} อ.{{ r.City }} จ.{{ r.Province }} {{ r.Zipcode }}</template>
              </li>
            </ul>
          </div>
        </div>

        <div class="row">
          <div class="col-md-3">
            <base-input :label="$t('member_admin.subdistrict')" v-model="addrForm.subdistrict" readonly></base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('member_admin.district')" v-model="addrForm.district" readonly></base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('member_admin.province')" v-model="addrForm.province" readonly></base-input>
          </div>
          <div class="col-md-3">
            <base-input :label="$t('member_admin.postcode')" v-model="addrForm.postcode" readonly></base-input>
          </div>
        </div>

        <div class="row">
          <div class="col-md-12">
            <base-checkbox v-model="addrIsDefault">
              {{ $t('member_admin.set_default') }}
            </base-checkbox>
          </div>
        </div>

        <div class="text-right mt-3">
          <base-button type="link" @click="addrModal.show = false" :disabled="addrModal.saving">
            {{ $t('cancel') }}
          </base-button>
          <base-button type="primary" :disabled="addrModal.saving || !addrForm.address" @click="saveAddress">
            <i class="fa fa-save"></i> {{ $t('save') }}
          </base-button>
        </div>
      </card>
    </modal>

    <!-- Order detail modal -->
    <modal :show.sync="orderModal.show" body-classes="p-0" modal-classes="modal-xl">
      <card type="secondary" header-classes="bg-white pb-3" body-classes="px-lg-4 py-lg-4" class="border-0 mb-0"
            v-loading="orderModal.loading">
        <h3 class="mb-2">
          {{ $t('member_admin.order_detail') || 'Order Detail' }}
          <span v-if="orderModal.data.ID" class="text-muted ml-2">#{{ orderModal.data.ID }}</span>
        </h3>

        <div class="row mb-2" v-if="orderModal.data.ID">
          <div class="col-md-3"><b>{{ $t('status') }}:</b> {{ orderStatusText(orderModal.data.order_status) }}</div>
          <div class="col-md-3"><b>{{ $t('member_admin.platform') || 'Platform' }}:</b> {{ platformText(orderModal.data.platform_id) }}</div>
          <div class="col-md-3"><b>{{ $t('member_admin.sale_channel') || 'Sale channel' }}:</b> {{ orderModal.data.bo_sale_channel || '-' }}</div>
          <div class="col-md-3"><b>{{ $t('member_admin.created') || 'Created' }}:</b> {{ fmtDateTime(orderModal.data.created_at) }}</div>
        </div>
        <div class="row mb-2" v-if="orderModal.data.ID">
          <div class="col-md-6"><b>{{ $t('member_admin.recipient_name') || 'Recipient' }}:</b> {{ orderModal.data.recipient_name || '-' }}</div>
          <div class="col-md-6"><b>{{ $t('member_admin.recipient_phone') || 'Phone' }}:</b> {{ orderModal.data.recipient_phone || '-' }}</div>
        </div>
        <div class="row mb-2" v-if="orderModal.data.ID">
          <div class="col-md-12"><b>{{ $t('member_admin.recipient_address') || 'Address' }}:</b> {{ recipientAddress }}</div>
        </div>
        <div class="row mb-2" v-if="orderModal.data.ID">
          <div class="col-md-6"><b>{{ $t('member_admin.tracking') || 'Tracking' }}:</b> {{ orderModal.data.shipping_tracking_number || '-' }}</div>
          <div class="col-md-3"><b>{{ $t('member_admin.amount') || 'Amount' }}:</b> {{ fmtNum(orderModal.data.amount) }}</div>
          <div class="col-md-3"><b>{{ $t('member_admin.pay_amount') || 'Pay' }}:</b> {{ fmtNum(orderModal.data.pay_amount) }}</div>
        </div>
        <div class="row mb-3" v-if="orderModal.data.ID">
          <div class="col-md-6"><b>{{ $t('member_admin.shipping_cost') || 'Shipping' }}:</b> {{ fmtNum(orderModal.data.shipping_cost) }}</div>
          <div class="col-md-6"><b>{{ $t('member_admin.discount') || 'Discount' }}:</b> {{ fmtNum(orderModal.data.discount) }}</div>
        </div>

        <!-- Promotions + WMS products are collapsible (default hidden) so the
             modal opens compact and the staff can expand the section they
             need without scrolling past long tables. -->
        <el-collapse v-model="orderModal.openPanels" class="mt-3">

          <el-collapse-item name="promotions">
            <template slot="title">
              <span class="font-weight-bold">{{ $t('member_admin.promotions') || 'Promotions' }}</span>
              <span class="text-muted ml-2 small">({{ orderModal.promotions.length }})</span>
            </template>
            <el-table :data="orderModal.promotions" size="mini" stripe class="compact-rows" v-if="orderModal.promotions.length">
              <el-table-column :label="$t('member_admin.product') || 'Product'" prop="name" min-width="300"></el-table-column>
              <el-table-column label="จำนวน" align="right" width="120">
                <template slot-scope="{row}">{{ fmtNum(row.quantity) }}</template>
              </el-table-column>
            </el-table>
            <div v-else class="text-muted small">{{ $t('member_admin.no_promotions') || 'No promotion data.' }}</div>
          </el-collapse-item>

          <el-collapse-item name="wms">
            <template slot="title">
              <span class="font-weight-bold">{{ $t('member_admin.wms_products') || 'WMS Products' }}</span>
              <span class="text-muted ml-2 small">({{ orderModal.wmsProducts.length }})</span>
            </template>
            <el-table :data="orderModal.wmsProducts" size="mini" stripe class="compact-rows" v-if="orderModal.wmsProducts.length">
              <el-table-column label="#" prop="wms_product_id" width="80"></el-table-column>
              <el-table-column :label="$t('member_admin.wms_product_name') || 'Name'" prop="wms_product_name" min-width="280"></el-table-column>
              <el-table-column :label="$t('member_admin.barcode') || 'Barcode'" prop="barcode" min-width="160"></el-table-column>
              <el-table-column label="จำนวน" align="right" width="100" prop="pickup_quantity"></el-table-column>
            </el-table>
            <div v-else class="text-muted small">{{ $t('member_admin.no_wms_products') || 'No WMS product data.' }}</div>
          </el-collapse-item>

        </el-collapse>

        <div class="text-right mt-4">
          <base-button type="link" @click="orderModal.show = false">{{ $t('close') }}</base-button>
        </div>
      </card>
    </modal>

    <!-- Coin adjust dialog (D3-3). A negative amount is a real operation, so
         there is one signed field rather than an add/subtract toggle; the note
         is mandatory because it becomes the audit trail ("<note> · by <user>")
         next to actor_user_id. The submit button stays disabled while
         validateAdjust reports anything, and the error text under the fields
         updates as the operator types. -->
    <modal :show.sync="coinAdjust.show" modal-classes="modal-secondary">
      <template slot="header">
        <h5 class="modal-title">{{ $t('member_coins.adjust_title') }}</h5>
      </template>

      <div class="text-muted small mb-3">
        {{ $t('member_coins.adjust_hint') }}
        <span class="d-block mt-1">
          {{ $t('member_coins.balance') }}: <b>{{ fmtNum(coinBalance) }}</b>
        </span>
      </div>

      <div class="row">
        <div class="col-md-6">
          <base-input :label="$t('member_coins.adjust_coins')">
            <el-input-number
              v-model="coinAdjust.coins"
              :min="-maxAdjustCoins"
              :max="maxAdjustCoins"
              :step="1"
              :precision="0"
              controls-position="right"
              style="width: 100%"
            ></el-input-number>
          </base-input>
        </div>
        <div class="col-md-6">
          <base-input :label="$t('member_coins.adjust_preview')"
                      :value="coinAdjustPreview"
                      readonly></base-input>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <base-input :label="$t('member_coins.adjust_note')">
            <el-input
              v-model="coinAdjust.note"
              type="textarea"
              :rows="2"
              maxlength="200"
              :placeholder="$t('member_coins.adjust_note_placeholder')"
            ></el-input>
          </base-input>
        </div>
      </div>

      <div v-if="coinAdjustError" class="alert alert-warning py-2 mb-0">
        <i class="fa fa-exclamation-triangle mr-1"></i>
        {{ $t('member_coins.err.' + coinAdjustError) }}
      </div>

      <template slot="footer">
        <button type="button" class="btn btn-md btn-primary"
                :disabled="!!coinAdjustError || coinAdjust.saving"
                @click="submitCoinAdjust()">
          <i class="fa fa-save mr-1"></i> {{ $t('save') || 'Save' }}
        </button>
        <button type="button" class="btn btn-md btn-link ml-auto"
                :disabled="coinAdjust.saving"
                @click="coinAdjust.show = false">
          {{ $t('close') || 'Close' }}
        </button>
      </template>
    </modal>

    <!-- Birthday modal (Buddhist year), mirrors OrderForm.vue. -->
    <modal :show.sync="showBirthdayModal" modal-classes="modal-secondary">
      <template slot="header">
        <h5 class="modal-title">{{ $t('birthday_modal_title') || 'Set birthday (Buddhist year)' }}</h5>
      </template>

      <div class="text-muted small mb-3">
        {{ $t('birthday_modal_hint') || 'Enter day, month and Buddhist-era year' }}
      </div>

      <div class="row">
        <div class="col-md-3">
          <base-input :label="$t('day') || 'Day'">
            <el-select v-model="birthdayInput.day" filterable>
              <el-option v-for="d in 31" :key="d" :label="d" :value="d"></el-option>
            </el-select>
          </base-input>
        </div>
        <div class="col-md-5">
          <base-input :label="$t('month') || 'Month'">
            <el-select v-model="birthdayInput.month" filterable>
              <el-option v-for="(name, idx) in monthOptions" :key="idx + 1" :label="name" :value="idx + 1"></el-option>
            </el-select>
          </base-input>
        </div>
        <div class="col-md-4">
          <base-input :label="$t('year_be') || 'Year (BE)'">
            <el-select v-model="birthdayInput.yearBE" filterable>
              <el-option v-for="y in beYearOptions" :key="y" :label="y" :value="y"></el-option>
            </el-select>
          </base-input>
        </div>
      </div>

      <div v-if="birthdayPreviewCE" class="alert alert-info py-2 mb-0">
        {{ $t('birthday') || 'Birthday' }}: {{ formatBirthdayBE(birthdayPreviewCE) }}
        <span class="text-muted small ml-2">(CE: {{ birthdayPreviewCE }})</span>
      </div>

      <template slot="footer">
        <button type="button" class="btn btn-md btn-primary" :disabled="!birthdayPreviewCE" @click="saveBirthday()">
          {{ $t('save') || 'Save' }}
        </button>
        <button type="button" class="btn btn-md btn-link ml-auto" @click="showBirthdayModal = false">
          {{ $t('close') || 'Close' }}
        </button>
      </template>
    </modal>
  </div>
</template>

<script>
import { Table, TableColumn, Tabs, TabPane, DatePicker, Collapse, CollapseItem, Select, Option, Input, InputNumber } from "element-ui";
import swal from "sweetalert2";
import { mapActions, mapGetters } from "vuex";
import MemberProvider from "@/resource/member";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import StatsCard from "@/components/Cards/StatsCard";
import { BaseCheckbox } from "@/components";
import { apiErrorMessage } from "@/util/api-error";
import AffiliateEligibilityPanel from "@/views/Marketing/components/AffiliateEligibilityPanel.vue";
import {
  validateAdjust,
  normalizeAdjustCoins,
  adjustApiErrorKey,
  coinRowMeta,
  MAX_ADJUST_COINS,
} from "@/utils/memberCoins";

const member = new MemberProvider();

// Default created_at window for the Orders tab — last 365 days ending today,
// floored at 2026-01-01 (the date the new spending model went live; orders
// before that aren't indexed by member_order). Returned as the [start, end]
// tuple el-date-picker daterange expects.
const ORDERS_FLOOR = "2026-01-01";
function defaultOrderRange() {
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const today = new Date();
  const start = new Date(today.getTime() - 365 * 24 * 60 * 60 * 1000);
  const startStr = fmt(start);
  return [startStr < ORDERS_FLOOR ? ORDERS_FLOOR : startStr, fmt(today)];
}

export default {
  components: {
    RouteBreadCrumb,
    StatsCard,
    BaseCheckbox,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Tabs.name]: Tabs,
    [TabPane.name]: TabPane,
    [DatePicker.name]: DatePicker,
    [Collapse.name]: Collapse,
    [CollapseItem.name]: CollapseItem,
    [Select.name]: Select,
    [Option.name]: Option,
    [Input.name]: Input,
    [InputNumber.name]: InputNumber,
    AffiliateEligibilityPanel,
  },
  data() {
    return {
      loading: false,
      activeTab: "profile",
      profile: {},
      level: null,
      pointsBalance: null,
      // Editable profile fields. `birthday` is the CE date string YYYY-MM-DD;
      // the BE modal converts to/from it via openBirthdayModal/saveBirthday.
      // Address fields mirror DBMember columns (subdistrict / district /
      // province / postcode) so the existing AdminUpdate endpoint accepts the
      // payload unchanged.
      edit: {
        fname: "", lname: "", phone: "", email: "",
        birthday: "",
        address: "",
        subdistrict: "", subdistrict_id: 0,
        district: "", district_id: 0,
        province: "", province_id: 0,
        postcode: "",
        // 1=company (default), 2=agent, 3=platform. Backend validates
        // the range in MemberAdminUpdate.
        member_type: 1,
      },
      // Inline address autocomplete on the profile tab. Separate from the
      // address-modal autocomplete state (addrQuery/addrOpen) so the two
      // controls don't fight over the same v-model.
      profileAddrQuery: "",
      profileAddrOpen: false,
      // Buddhist-year modal state (mirrors OrderForm.vue).
      showBirthdayModal: false,
      birthdayInput: { day: "", month: "", yearBE: "" },
      addresses: [], loadingAddresses: false,
      addrModal: { show: false, saving: false },
      orderModal: {
        show: false,
        loading: false,
        data: {},
        promotions: [],
        wmsProducts: [],
        // open panel names for el-collapse — array of "promotions"/"wms".
        // Empty array = both collapsed (default state when the popup opens).
        openPanels: [],
      },
      addrForm: this.emptyAddrForm(),
      addrQuery: "",
      addrOpen: false,
      points: [],    loadingPoints: false,
      // Coin ledger (D3-3). coinBalance stays null until the API answers with a
      // coin_balance — null is "this deployment has no coin ledger", which is
      // what hides the profile field and keeps this page working on an older
      // API. 0 is a real balance and must still render.
      coinBalance: null,
      coinLifetimeEarned: 0,
      coinRows: [],
      loadingCoins: false,
      coinAdjust: { show: false, saving: false, coins: null, note: "" },
      coupons: [],   loadingCoupons: false,
      redemptions: [], loadingRedemptions: false,
      orders: [],    loadingOrders: false,
      // Orders tab is search-driven (date range + phone match) — no auto-load.
      // `range` is [startISO, endISO] from el-date-picker daterange (or null).
      // ordersSearched flips true after the first explicit Search so the
      // empty table shows "no results" instead of the initial hint.
      ordersFilter: { range: defaultOrderRange() },
      ordersSearched: false,
      notifications: [], loadingNotifications: false,
      sessions: [],  loadingSessions: false,
      events: [],    loadingEvents: false,
      pdpa: [],      loadingPDPA: false,
      legacy: [],    loadingLegacy: false,
    };
  },
  computed: {
    ...mapGetters({
      canEdit: "Auth/isEditMember",
      isAccessMarketing: "Auth/isAccessMarketing",
      isAccessAffiliateFinance: "Auth/isAccessAffiliateFinance",
      FullAddr: "Order/getAddress",
    }),
    canSeeAffiliate() {
      return !!(this.isAccessMarketing || this.isAccessAffiliateFinance);
    },
    // Joined recipient address line for the order-detail popup. Uses the
    // bo_orders.recipient_address_fulladdress when populated; otherwise
    // composes from the individual fields.
    recipientAddress() {
      const d = this.orderModal.data || {};
      const full = (d.recipient_address_fulladdress || "").trim();
      if (full) return full;
      const parts = [
        d.recipient_address_detail,
        d.recipient_address_district,
        d.recipient_address_city,
        d.recipient_address_province,
        d.recipient_zipcode,
      ].filter(Boolean);
      return parts.length ? parts.join(" ") : "-";
    },
    addrIsDefault: {
      get() { return this.addrForm.is_default == 1; },
      set(v) { this.addrForm.is_default = v ? 1 : 0; },
    },
    filteredAddress() {
      if (!this.addrQuery) return [];
      const key = this.addrQuery.split(" ");
      const result = (this.FullAddr || []).filter((item) => {
        if (key.length == 1) {
          return item.District.includes(key[0]) || item.Zipcode == key[0];
        }
        if (key.length == 2) {
          return (item.District.includes(key[0]) || item.Zipcode == key[0]) && item.City.includes(key[1]);
        }
        if (key.length >= 3) {
          return (item.District.includes(key[0]) || item.Zipcode == key[0]) && item.City.includes(key[1]) && item.Province.includes(key[2]);
        }
        return false;
      });
      return result.slice(0, 15);
    },
    // Same filter shape as filteredAddress, just keyed on profileAddrQuery so
    // the profile-tab autocomplete is independent of the address-modal one.
    profileFilteredAddress() {
      if (!this.profileAddrQuery) return [];
      const key = this.profileAddrQuery.split(" ");
      const result = (this.FullAddr || []).filter((item) => {
        if (key.length == 1) {
          return item.District.includes(key[0]) || item.Zipcode == key[0];
        }
        if (key.length == 2) {
          return (item.District.includes(key[0]) || item.Zipcode == key[0]) && item.City.includes(key[1]);
        }
        if (key.length >= 3) {
          return (item.District.includes(key[0]) || item.Zipcode == key[0]) && item.City.includes(key[1]) && item.Province.includes(key[2]);
        }
        return false;
      });
      return result.slice(0, 15);
    },
    monthOptions() {
      const th = ["มกราคม","กุมภาพันธ์","มีนาคม","เมษายน","พฤษภาคม","มิถุนายน","กรกฎาคม","สิงหาคม","กันยายน","ตุลาคม","พฤศจิกายน","ธันวาคม"];
      const en = ["January","February","March","April","May","June","July","August","September","October","November","December"];
      return this.$i18n && this.$i18n.locale === "en" ? en : th;
    },
    beYearOptions() {
      const currentBE = new Date().getFullYear() + 543;
      const years = [];
      for (let y = currentBE; y >= currentBE - 100; y--) years.push(y);
      return years;
    },
    birthdayPreviewCE() {
      const { day, month, yearBE } = this.birthdayInput;
      if (!day || !month || !yearBE) return "";
      const yearCE = parseInt(yearBE, 10) - 543;
      const m = String(month).padStart(2, "0");
      const d = String(day).padStart(2, "0");
      const dt = new Date(yearCE, parseInt(m, 10) - 1, parseInt(d, 10));
      if (
        isNaN(dt.getTime()) ||
        dt.getFullYear() !== yearCE ||
        dt.getMonth() + 1 !== parseInt(m, 10) ||
        dt.getDate() !== parseInt(d, 10)
      ) {
        return "";
      }
      return `${yearCE}-${m}-${d}`;
    },
    // Exposed so the el-input-number bounds come from the same constant the
    // validator uses — a widget that let you type 100001 and a validator that
    // refuses it would be two limits.
    maxAdjustCoins() {
      return MAX_ADJUST_COINS;
    },
    // Live validation: an error key while the form is not submittable, null
    // when it is. The template renders $t('member_coins.err.' + key).
    coinAdjustError() {
      return validateAdjust(this.coinAdjust);
    },
    // "500 → 600". Shown so the operator confirms the RESULT, not just the
    // delta — the mistake this dialog invites is a sign error. Purely
    // informational: the API computes the real balance and may still answer
    // 409 `insufficient`.
    coinAdjustPreview() {
      const coins = normalizeAdjustCoins(this.coinAdjust.coins);
      if (coins === null || this.coinBalance === null) return "-";
      const from = Number(this.coinBalance) || 0;
      return `${this.fmtNum(from)} → ${this.fmtNum(from + coins)}`;
    },
  },
  mounted() {
    this.loadProfile();
    // Coins load with the profile, not on tab click: the balance is shown on
    // the profile tab too, and a support agent should not have to open a tab to
    // see it.
    this.loadCoins();
    if ((this.FullAddr || []).length === 0) this.listFullAddr();
  },
  methods: {
    ...mapActions({ listFullAddr: "Order/listAddressV2" }),
    emptyAddrForm() {
      return {
        id: 0, fname: "", lname: "", phone: "", address: "",
        subdistrict: "", subdistrict_id: 0,
        district: "", district_id: 0,
        province: "", province_id: 0,
        postcode: "", note: "", is_default: 0,
      };
    },
    setAddrResult(r) {
      this.addrForm.subdistrict_id = r.DistrictID;
      this.addrForm.subdistrict = r.District;
      this.addrForm.district_id = r.CityID;
      this.addrForm.district = r.City;
      this.addrForm.province_id = r.ProvinceID;
      this.addrForm.province = r.Province;
      this.addrForm.postcode = r.Zipcode;
      this.addrQuery = "";
      this.addrOpen = false;
    },
    orderRowClass() {
      return "cursor-pointer";
    },
    // Per-line promotion amount: list price minus sale price times quantity,
    // plus any seller / platform discount on the line. Negative or NaN inputs
    // fall back to 0 so display stays clean for messy historical rows.
    promotionAmount(row) {
      const orig = Number(row.original_price) || 0;
      const sale = Number(row.sale_price) || 0;
      const qty  = Number(row.quantity) || 0;
      const sellerDisc = Number(row.seller_discount) || 0;
      const platDisc   = Number(row.platform_discount) || 0;
      const lineDiff = Math.max(orig - sale, 0) * qty;
      return lineDiff + sellerDisc + platDisc;
    },
    // OrderStatus enum mirror — keep in sync with controllers/const.go.
    orderStatusText(code) {
      const n = Number(code);
      const map = {
        100: this.$t("member_admin.os_unpaid")              || "Unpaid",
        111: this.$t("member_admin.os_awaiting_shipment")   || "Awaiting Shipment",
        112: this.$t("member_admin.os_awaiting_collection") || "Awaiting Collection",
        114: this.$t("member_admin.os_partially_shipping")  || "Partially Shipping",
        115: this.$t("member_admin.os_awaiting_transit")    || "Awaiting Transit",
        121: this.$t("member_admin.os_in_transit")          || "In Transit",
        122: this.$t("member_admin.os_delivered")           || "Delivered",
        130: this.$t("member_admin.os_completed")           || "Completed",
        140: this.$t("member_admin.os_cancelled")           || "Cancelled",
        141: this.$t("member_admin.os_returned")            || "Returned",
        142: this.$t("member_admin.os_claim")               || "Claim",
        143: this.$t("member_admin.os_returning")           || "Returning",
      };
      return map[n] || (code != null ? String(code) : "-");
    },
    // Platform enum mirror — keep in sync with controllers/const.go.
    platformText(code) {
      const n = Number(code);
      const map = {
        1:  "MadameFin",
        2:  "TikTok",
        3:  "Shopee",
        4:  "Lazada",
        5:  "Line",
        6:  "Facebook",
        7:  "Instagram",
        8:  "Pancake",
        9:  "Mdf App",
        10: "Mdf Web",
      };
      return map[n] || (code != null ? String(code) : "-");
    },
    async searchOrders() {
      if (!this.profile.id) return;
      const r = this.ordersFilter.range;
      // el-date-picker emits `null` when cleared via the (x) button.
      if (!Array.isArray(r) || r.length !== 2 || !r[0] || !r[1]) {
        this.orders = [];
        this.ordersSearched = false;
        return;
      }
      this.loadingOrders = true;
      this.ordersSearched = true;
      try {
        const res = await member.AdminOrders(this.profile.id, {
          date_from: r[0],
          date_to: r[1],
        });
        const data = (res.data && res.data.Data && res.data.Data[0]) || [];
        this.orders = Array.isArray(data) ? data : [];
      } catch (e) {
        console.log(e);
        const msg = (e.response && e.response.data && (e.response.data.Cause || e.response.data.Msg)) || "Failed";
        swal.fire({
          title: msg,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
        this.orders = [];
      }
      this.loadingOrders = false;
    },
    async openOrderDetail(orderID) {
      this.orderModal = {
        show: true,
        loading: true,
        data: {},
        promotions: [],
        wmsProducts: [],
        openPanels: [],
      };
      try {
        const res = await member.AdminOrderDetail(this.profile.id, orderID);
        // Backend r.Set(order) → response.data.Datum is the DBOrder.
        const order = (res.data && res.data.Datum) || {};
        this.orderModal.data = order;

        // product_list is a JSON string; parse defensively. The quantity
        // in product_list can be stale or 0 on historical rows — fall back
        // to the matching order_items entry, which is always populated by
        // the order pipeline.
        try {
          const pl = order.product_list ? JSON.parse(order.product_list) : null;
          const prods = (pl && pl.products) || [];
          // Index order_items by bo_product_id so each promotion line can pull
          // its authoritative quantity AND its sku-level name (the long SKU
          // descriptor that lists the variants inside a bundle).
          const itemByProductID = new Map();
          (order.order_items || []).forEach((oi) => {
            itemByProductID.set(oi.bo_product_id, oi);
          });
          this.orderModal.promotions = prods.map((p) => {
            const price = p.price || {};
            const oi = itemByProductID.get(p.id) || {};
            const fromItems = Number(oi.quantity) || 0;
            const skuName = oi.bo_product_sku_name || "";
            // "<promotion name> : <sku name>" — promotion name comes from
            // product_list, sku descriptor comes from order_items.
            const display = skuName ? `${p.name} : ${skuName}` : (p.name || "-");
            return {
              name: display,
              quantity: (fromItems > 0 ? fromItems : Number(p.quantity)) || 0,
              original_price: price.original_price,
              sale_price: price.sale_price,
              seller_discount: price.seller_discount || price.saller_discount,
              platform_discount: price.platform_discount,
            };
          });
        } catch (_) {
          this.orderModal.promotions = [];
        }

        // wms_product_summary is also a JSON string of an array.
        try {
          this.orderModal.wmsProducts = order.wms_product_summary
            ? JSON.parse(order.wms_product_summary)
            : [];
        } catch (_) {
          this.orderModal.wmsProducts = [];
        }
      } catch (e) {
        console.log(e);
        const msg = (e.response && e.response.data && (e.response.data.Cause || e.response.data.Msg)) || "Failed";
        swal.fire({
          title: msg,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
        this.orderModal.show = false;
      }
      this.orderModal.loading = false;
    },
    openAddressCreate() {
      this.addrForm = this.emptyAddrForm();
      // Prefill recipient from profile defaults to save typing.
      this.addrForm.fname = this.profile.fname || "";
      this.addrForm.lname = this.profile.lname || "";
      this.addrForm.phone = this.profile.phone || "";
      this.addrQuery = "";
      this.addrOpen = false;
      this.addrModal.show = true;
    },
    openAddressEdit(row) {
      this.addrForm = {
        id: row.id,
        fname: row.fname || "",
        lname: row.lname || "",
        phone: row.phone || "",
        address: row.address || "",
        subdistrict: row.subdistrict || "",
        subdistrict_id: row.subdistrict_id || 0,
        district: row.district || "",
        district_id: row.district_id || 0,
        province: row.province || "",
        province_id: row.province_id || 0,
        postcode: row.postcode || "",
        note: row.note || "",
        is_default: row.is_default || 0,
      };
      this.addrQuery = "";
      this.addrOpen = false;
      this.addrModal.show = true;
    },
    async saveAddress() {
      const id = this.profile.id;
      if (!id) return;
      this.addrModal.saving = true;
      try {
        const payload = {
          fname: this.addrForm.fname,
          lname: this.addrForm.lname,
          phone: this.addrForm.phone,
          address: this.addrForm.address,
          subdistrict: this.addrForm.subdistrict,
          subdistrict_id: Number(this.addrForm.subdistrict_id) || 0,
          district: this.addrForm.district,
          district_id: Number(this.addrForm.district_id) || 0,
          province: this.addrForm.province,
          province_id: Number(this.addrForm.province_id) || 0,
          postcode: this.addrForm.postcode,
          note: this.addrForm.note,
          is_default: this.addrForm.is_default ? 1 : 0,
        };
        if (this.addrForm.id) {
          await member.AdminAddressUpdate(id, this.addrForm.id, payload);
        } else {
          await member.AdminAddressCreate(id, payload);
        }
        this.addrModal.show = false;
        await this.reloadAddresses();
      } catch (e) {
        const msg = (e.response && e.response.data && (e.response.data.Cause || e.response.data.Msg)) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
      this.addrModal.saving = false;
    },
    async deleteAddress(row) {
      const ok = await swal.fire({
        title: this.$t("delete_confirm") || "Delete?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonClass: "btn btn-danger btn-fill",
        cancelButtonClass: "btn btn-secondary btn-fill",
        confirmButtonText: this.$t("confirm") || "OK",
        cancelButtonText: this.$t("cancel") || "Cancel",
        buttonsStyling: false,
      });
      if (!ok.isConfirmed) return;
      try {
        await member.AdminAddressDelete(this.profile.id, row.id);
        await this.reloadAddresses();
      } catch (e) {
        const msg = (e.response && e.response.data && (e.response.data.Cause || e.response.data.Msg)) || "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
    },
    async reloadAddresses() {
      this.loadingAddresses = true;
      try {
        const res = await member.AdminAddresses(this.profile.id);
        const data = (res.data && res.data.Data && res.data.Data[0]) || [];
        this.addresses = Array.isArray(data) ? data : [];
      } catch (e) { console.log(e); }
      this.loadingAddresses = false;
    },
    fmtNum(v) {
      if (v === null || v === undefined || v === "") return "-";
      return Number(v).toLocaleString("en-US");
    },
    // --- Coin ledger (D3-3) -------------------------------------------------
    // Thin wrappers over the pure helpers in src/utils/memberCoins.js so the
    // template never computes a sign, a colour or a label itself.
    coinMeta(row) {
      return coinRowMeta(row);
    },
    coinTypeBadgeClass(type) {
      const map = {
        earn: "badge-success",
        refund: "badge-success",
        burn: "badge-danger",
        clawback: "badge-danger",
        expire: "badge-warning",
        adjust: "badge-info",
      };
      return map[type] || "badge-secondary";
    },
    // "order #123" / "manual" — the reference is two columns in the ledger
    // (ref_type + ref_id or ref_uuid) and reads as one thing.
    coinRefText(row) {
      const t = (row && row.ref_type) || "";
      const id = (row && (row.ref_id || row.ref_uuid)) || "";
      if (!t && !id) return "-";
      if (!id) return t;
      return `${t} #${id}`;
    },
    async loadCoins() {
      const id = this.$route.params.id;
      if (!id) return;
      this.loadingCoins = true;
      try {
        const res = await member.AdminCoins(id, { limit: 200 });
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        // Only adopt a balance the API actually sent. `!= null` keeps 0.
        this.coinBalance = data.coin_balance != null ? Number(data.coin_balance) : null;
        this.coinLifetimeEarned = Number(data.lifetime_coins_earned) || 0;
        this.coinRows = Array.isArray(data.rows) ? data.rows : [];
      } catch (e) {
        // A 404 here means the coin endpoints are not deployed yet — the coin
        // field and this tab stay empty rather than throwing a popup at every
        // member page load.
        console.log(e);
      }
      this.loadingCoins = false;
    },
    openCoinAdjust() {
      this.coinAdjust = { show: true, saving: false, coins: null, note: "" };
    },
    async submitCoinAdjust() {
      // The dialog already disables the button, but validate again: this is the
      // last gate before a balance moves.
      if (this.coinAdjustError) return;
      const id = this.profile.id || this.$route.params.id;
      this.coinAdjust.saving = true;
      try {
        const res = await member.AdminCoinsAdjust(id, {
          coins: normalizeAdjustCoins(this.coinAdjust.coins),
          note: String(this.coinAdjust.note || "").trim(),
        });
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        if (data.coin_balance != null) this.coinBalance = Number(data.coin_balance);
        this.coinAdjust.show = false;
        swal.fire({
          title: this.$t("member_coins.adjust_success"),
          icon: "success",
          timer: 2000,
          buttonsStyling: false,
          confirmButtonClass: "btn btn-success btn-fill",
        });
        // Re-read the ledger so the new `adjust` row (and the balance the API
        // computed, not the one we guessed) is what the table shows.
        await this.loadCoins();
      } catch (e) {
        // 400 bad_adjust / 409 insufficient carry their code in `Msg`; anything
        // else goes through the shared message mapper.
        const key = adjustApiErrorKey(e);
        const msg = key
          ? this.$t("member_coins.err." + key)
          : apiErrorMessage(e, this.$t.bind(this));
        swal.fire({
          title: msg,
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
      }
      this.coinAdjust.saving = false;
    },
    fmtDate(d) { return d ? String(d).substring(0, 10) : "-"; },
    fmtDateTime(d) { return d ? String(d).substring(0, 19).replace("T", " ") : "-"; },
    async loadProfile() {
      this.loading = true;
      try {
        const res = await member.AdminGet(this.$route.params.id);
        const data = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.profile = data.profile || {};
        this.level = data.level || null;
        this.pointsBalance = data.points_balance != null ? data.points_balance : null;
        // birthday from API is an ISO datetime; only keep YYYY-MM-DD and
        // discard placeholder dates (year < 1900 / > 9999) the old PHP layer
        // sometimes wrote.
        let bday = "";
        if (this.profile.birthday) {
          const ce = String(this.profile.birthday).substring(0, 10);
          const y = parseInt(ce.substring(0, 4), 10);
          if (y > 1900 && y < 9999) bday = ce;
        }
        this.edit = {
          fname: this.profile.fname || "",
          lname: this.profile.lname || "",
          phone: this.profile.phone || "",
          email: this.profile.email || "",
          birthday: bday,
          address: this.profile.address || "",
          subdistrict: this.profile.subdistrict || "",
          subdistrict_id: this.profile.subdistrict_id || 0,
          district: this.profile.district || "",
          district_id: this.profile.district_id || 0,
          province: this.profile.province || "",
          province_id: this.profile.province_id || 0,
          postcode: this.profile.postcode || "",
          // Default Company (1) for legacy rows where the column was
          // backfilled — but always show what the server returned.
          member_type: Number(this.profile.member_type) || 1,
        };
      } catch (e) { console.log(e); }
      this.loading = false;
    },
    async save() {
      // Thai mobile pattern: 10 digits starting with 0. Same regex as
      // OrderForm.vue's recipient_phone check, so the two flows agree.
      const phone = String(this.edit.phone || "").trim();
      if (!/^0\d{9}$/.test(phone)) {
        swal.fire({
          title: this.$t("phone_invalid") || "Invalid phone",
          icon: "error",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-danger btn-fill",
        });
        return;
      }
      try {
        // Send birthday as YYYY-MM-DD; backend MemberAdminUpdate already
        // accepts birthday + the address fields in its allowed-keys map.
        await member.AdminUpdate(this.profile.id, this.edit);
        swal.fire({
          title: this.$t("update_success"),
          icon: "success",
          buttonsStyling: false,
          confirmButtonClass: "btn btn-success btn-fill",
        });
        this.loadProfile();
      } catch (e) {
        const msg =
          (e.response && e.response.data && (e.response.data.Cause || e.response.data.Msg)) ||
          this.$t("update_failed") ||
          "Failed";
        swal.fire({ title: msg, icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
      }
    },
    setProfileAddrResult(r) {
      // FullAddr exposes Thai admin levels as District (tambon) / City
      // (amphoe) / Province / Zipcode — map them onto the member schema:
      //   subdistrict = tambon, district = amphoe, province = province.
      this.edit.subdistrict_id = r.DistrictID;
      this.edit.subdistrict = r.District;
      this.edit.district_id = r.CityID;
      this.edit.district = r.City;
      this.edit.province_id = r.ProvinceID;
      this.edit.province = r.Province;
      this.edit.postcode = r.Zipcode;
      this.profileAddrQuery = "";
      this.profileAddrOpen = false;
    },
    openBirthdayModal() {
      if (this.edit.birthday) {
        const [y, m, d] = this.edit.birthday.split("-").map((s) => parseInt(s, 10));
        if (y && m && d) {
          this.birthdayInput = { day: d, month: m, yearBE: y + 543 };
        }
      } else {
        this.birthdayInput = { day: "", month: "", yearBE: "" };
      }
      this.showBirthdayModal = true;
    },
    clearBirthday() {
      this.edit.birthday = "";
      this.birthdayInput = { day: "", month: "", yearBE: "" };
    },
    saveBirthday() {
      if (!this.birthdayPreviewCE) {
        swal.fire({ title: this.$t("birthday_invalid") || "Invalid birthday", icon: "error", buttonsStyling: false, confirmButtonClass: "btn btn-danger btn-fill" });
        return;
      }
      this.edit.birthday = this.birthdayPreviewCE;
      this.showBirthdayModal = false;
    },
    formatBirthdayBE(ce) {
      if (!ce) return "";
      const [y, m, d] = ce.split("-").map((s) => parseInt(s, 10));
      if (!y || !m || !d) return "";
      const months = this.monthOptions;
      return `${d} ${months[m - 1]} ${y + 543}`;
    },
    onTabChange(tab) {
      const id = this.profile.id;
      if (!id) return;
      // Orders tab now auto-fires the search with whatever range is in the
      // picker (defaulted to last 365 days, floored at 2026-01-01). Staff
      // can still tweak the range and re-search.
      if (tab.name === "orders") {
        if (!this.ordersSearched) this.searchOrders();
        return;
      }
      // Coins are fetched on mount (the profile tab shows the balance too), so
      // this only retries a load that failed or came back empty.
      if (tab.name === "coins") {
        if (!this.coinRows.length) this.loadCoins();
        return;
      }
      const map = {
        addresses:     ["loadingAddresses",     "addresses",     () => member.AdminAddresses(id)],
        points:        ["loadingPoints",        "points",        () => member.AdminPoints(id, { limit: 200 })],
        coupons:       ["loadingCoupons",       "coupons",       () => member.AdminCoupons(id)],
        redemptions:   ["loadingRedemptions",   "redemptions",   () => member.AdminRedemptions(id)],
        notifications: ["loadingNotifications", "notifications", () => member.AdminNotifications(id)],
        sessions:      ["loadingSessions",      "sessions",      () => member.AdminSessions(id)],
        events:        ["loadingEvents",        "events",        () => member.AdminEvents(id, { limit: 200 })],
        pdpa:          ["loadingPDPA",          "pdpa",          () => member.AdminPDPA(id)],
        legacy:        ["loadingLegacy",        "legacy",        () => member.AdminLegacyLink(id)],
      };
      const m = map[tab.name];
      if (!m) return;
      const [loadingKey, dataKey, fn] = m;
      if (this[dataKey].length > 0) return;
      this[loadingKey] = true;
      fn().then(res => {
        const data = (res.data && res.data.Data && res.data.Data[0]) || [];
        if (tab.name === "points") {
          this[dataKey] = data.log || [];
          if (data.balance != null) this.pointsBalance = data.balance;
        } else {
          this[dataKey] = Array.isArray(data) ? data : [];
        }
      }).catch(e => console.log(e)).finally(() => { this[loadingKey] = false; });
    },
  },
};
</script>

<style scoped>
/* Autocomplete dropdown for the address-search input. */
.autocomplete-results {
  padding: 0;
  margin: 0;
  border: 1px solid #eeeeee;
  min-height: 1em;
  max-height: 9em;
  overflow: auto;
}
.autocomplete-result {
  list-style: none;
  text-align: left;
  padding: 4px 6px;
  cursor: pointer;
}
.autocomplete-result:hover,
.autocomplete-result.is-active {
  background-color: #4aae9b;
  color: white;
}
.cursor-pointer {
  cursor: pointer;
}
</style>

<style>
/* Profile detail card: make the value larger than the label for readonly
   summary fields, so the data the user actually wants reads first. */
.member-detail-card .form-control-label {
  font-size: 0.75rem;
  /* was #8898aa = 2.95:1 on the white card, at 0.75rem uppercase -> #607286 = 4.94:1 */
  color: #607286;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.15rem;
}
.member-detail-card .form-control[readonly],
.member-detail-card .form-control:read-only {
  font-size: 1.05rem;
  font-weight: 600;
  color: #32325d;
  background: transparent;
  border: 0;
  border-bottom: 1px solid #e9ecef;
  border-radius: 0;
  padding-left: 0;
  padding-right: 0;
}

/* Tight row spacing for the order-detail popup's collapsible tables.
   el-table size="mini" still pads ~6px top/bottom; halve that. */
.compact-rows.el-table .el-table__row td,
.compact-rows.el-table th {
  padding-top: 2px;
  padding-bottom: 2px;
  line-height: 1.2;
}
.compact-rows.el-table .cell {
  padding-top: 0;
  padding-bottom: 0;
}
</style>
